import React from 'react';

/**
 * Utilidades para cargar iconos de manera eficiente
 * 
 * Este archivo proporciona funciones para trabajar con react-icons
 * de manera optimizada, reduciendo significativamente el tamaño del bundle.
 * Ahora usa dynamic imports para cargar solo los iconos necesarios.
 */

/**
 * Familias de iconos soportadas
 */
const supportedFamilies = [
  'md', 'fa', 'fa6', 'bs', 'bi', 'ri', 'fi', 'vsc', 'rx', 
  'hi', 'hi2', 'go', 'gi', 'cg', 'si', 'io', 'io5', 'tb'
];

// Componente de icono de respaldo para casos donde no se encuentra el icono
const FallbackIcon = (props) => (
  <span 
    {...props} 
    style={{ 
      display: 'inline-block',
      width: '1em',
      height: '1em',
      backgroundColor: '#e5e7eb',
      borderRadius: '50%',
      ...props.style
    }}
  />
);

/**
 * Mapa de dynamic imports para cada familia de iconos
 */
const familyImports = {
  md: () => import('react-icons/md'),
  fa: () => import('react-icons/fa'),
  fa6: () => import('react-icons/fa6'),
  bs: () => import('react-icons/bs'),
  bi: () => import('react-icons/bi'),
  ri: () => import('react-icons/ri'),
  fi: () => import('react-icons/fi'),
  vsc: () => import('react-icons/vsc'),
  rx: () => import('react-icons/rx'),
  hi: () => import('react-icons/hi'),
  hi2: () => import('react-icons/hi2'),
  go: () => import('react-icons/go'),
  gi: () => import('react-icons/gi'),
  cg: () => import('react-icons/cg'),
  si: () => import('react-icons/si'),
  io: () => import('react-icons/io'),
  io5: () => import('react-icons/io5'),
  tb: () => import('react-icons/tb'),
};

/**
 * Obtiene un icono específico por su familia y nombre usando dynamic import
 * @param {string} family - Familia de iconos (md, fa, bs, etc.)
 * @param {string} iconName - Nombre del icono
 * @returns {Object} - El componente del icono o un fallback si no se encuentra
 */
export const loadIcon = async (family, iconName) => {
  try {
    const familyKey = family.toLowerCase();
    
    if (!familyImports[familyKey]) {
      console.warn(`Familia de iconos no soportada: ${family}, usando fallback`);
      return FallbackIcon;
    }
    
    // Dynamic import para cargar solo la familia necesaria
    const iconModule = await familyImports[familyKey]();
    
    const icon = iconModule[iconName];
    if (!icon) {
      console.warn(`Icono no encontrado: ${iconName} en la familia ${family}, usando fallback`);
      return FallbackIcon;
    }
    
    return icon;
  } catch (error) {
    console.error(`Error al cargar el icono ${family}/${iconName}:`, error);
    return FallbackIcon;
  }
};

/**
 * Cargar múltiples iconos usando dynamic imports
 * @param {Array<{family: string, name: string}>} iconConfigs - Configuraciones de iconos
 * @returns {Object} - Objeto con los iconos cargados
 */
export const loadIcons = async (iconConfigs) => {
  try {
    const results = {};
    
    // Agrupar por familia para hacer menos dynamic imports
    const familyGroups = iconConfigs.reduce((acc, { family, name }) => {
      const familyKey = family.toLowerCase();
      if (!acc[familyKey]) acc[familyKey] = [];
      acc[familyKey].push(name);
      return acc;
    }, {});
    
    // Cargar cada familia una sola vez
    for (const [familyKey, iconNames] of Object.entries(familyGroups)) {
      if (!familyImports[familyKey]) {
        console.warn(`Familia de iconos no soportada: ${familyKey}, usando fallback`);
        iconNames.forEach(name => {
          results[name] = FallbackIcon;
        });
        continue;
      }
      
      try {
        // Dynamic import para cargar solo la familia necesaria
        const iconModule = await familyImports[familyKey]();
        
        iconNames.forEach(name => {
          const icon = iconModule[name];
          if (icon) {
            results[name] = icon;
          } else {
            console.warn(`Icono no encontrado: ${name} en la familia ${familyKey}, usando fallback`);
            results[name] = FallbackIcon;
          }
        });
      } catch (error) {
        console.error(`Error al cargar familia de iconos ${familyKey}:`, error);
        iconNames.forEach(name => {
          results[name] = FallbackIcon;
        });
      }
    }
    
    return results;
  } catch (error) {
    console.error("Error al cargar iconos:", error);
    // Devolver al menos un objeto vacío para evitar errores
    return iconConfigs.reduce((acc, { name }) => {
      acc[name] = FallbackIcon;
      return acc;
    }, {});
  }
};

/**
 * @deprecated Use loadIcon o el componente OptimizedIcon
 */
export const optimizedIconImport = (iconPath) => {
  console.warn('optimizedIconImport está obsoleto. Use loadIcon o el componente OptimizedIcon.');
  const parts = iconPath.split('/');
  const family = parts[0];
  const iconName = parts[1];
  
  return loadIcon(family, iconName);
}; 